import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight, Download, Cable, Monitor, Network, Camera, Server,
  CheckCircle, Package, TrendingDown, Truck, Users, ShieldCheck
} from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";

const HERO_SCENES = [
  {
    label: "Server Room",
    Icon: Server,
    img: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=520&q=80&auto=format&fit=crop",
  },
  {
    label: "Conference Setup",
    Icon: Monitor,
    img: "https://images.unsplash.com/photo-1517502884422-41eaead166d4?w=520&q=80&auto=format&fit=crop",
  },
  {
    label: "CCTV Surveillance",
    Icon: Camera,
    img: "https://images.pexels.com/photos/207574/pexels-photo-207574.jpeg?auto=compress&cs=tinysrgb&w=520",
  },
];

function HeroSceneCarousel() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx(i => (i + 1) % HERO_SCENES.length), 3500);
    return () => clearInterval(t);
  }, []);
  const scene = HERO_SCENES[idx];
  const Icon = scene.Icon;

  return (
    <div
      className="absolute -bottom-8 -right-6 sm:-right-8 w-[190px] sm:w-[220px] shadow-2xl border-2 border-white bg-white overflow-hidden"
      data-testid="hero-scene-carousel"
    >
      <div className="relative w-full h-[130px] overflow-hidden bg-gray-100">
        <AnimatePresence mode="wait">
          <motion.img
            key={scene.img}
            src={scene.img}
            alt={scene.label}
            initial={{ opacity: 0, scale: 1.08 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1 }}
            transition={{ duration: 0.7 }}
            className="absolute inset-0 w-full h-full object-cover"
          />
        </AnimatePresence>
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
        <div className="absolute bottom-2 left-2 flex items-center gap-1.5 text-white">
          <div className="p-1 bg-[#0F62FE]"><Icon size={11} /></div>
          <span className="text-[10px] font-bold uppercase tracking-widest">{scene.label}</span>
        </div>
      </div>
      {/* progress dots */}
      <div className="flex items-center justify-center gap-1.5 py-2 bg-white">
        {HERO_SCENES.map((s, i) => (
          <span
            key={s.label}
            className={`h-1 rounded-full transition-all duration-300 ${
              i === idx ? "w-5 bg-[#0F62FE]" : "w-1.5 bg-gray-300"
            }`}
          />
        ))}
      </div>
    </div>
  );
}


const STATS = [
  { value: "07+", label: "Years Experience" },
  { value: "2000+", label: "Products in Stock" },
  { value: "1000+", label: "Satisfied Clients" },
  { value: "11", label: "Brand Partners" },
];

const CATEGORIES = [
  { id: "cables", name: "Cables & Connectivity", Icon: Cable, desc: "HDMI, VGA, DisplayPort, USB & power cables", count: "50+ Products", color: "#0F62FE", bg: "#EFF6FF",
    img: "https://static.prod-images.emergentagent.com/jobs/807d24b9-8a0d-4ee2-b221-042517f188cc/images/f6f8147bf636535a69696aa8501aad4936c337c5b5afa664abf153101ce988f2.png" },
  { id: "peripherals", name: "Peripherals & Audio", Icon: Monitor, desc: "Quantron keyboards, mice, headsets & webcams", count: "40+ Products", color: "#7C3AED", bg: "#F5F3FF",
    img: "https://static.prod-images.emergentagent.com/jobs/807d24b9-8a0d-4ee2-b221-042517f188cc/images/081e7c3f9154fcb51f4553dce7b86a9e5e7f450b3b28c8c43764210285c4bbd1.png" },
  { id: "networking", name: "Networking Infrastructure", Icon: Network, desc: "Cat6, PoE switches, patch panels & routers", count: "45+ Products", color: "#059669", bg: "#ECFDF5",
    img: "https://static.prod-images.emergentagent.com/jobs/807d24b9-8a0d-4ee2-b221-042517f188cc/images/a07deec238185167a9b66ddb86bad4ebd709e7b691f73a94c79759c68cd49cee.png" },
  { id: "cctv", name: "CCTV & Surveillance", Icon: Camera, desc: "Hikvision cameras, DVR systems & racks", count: "35+ Products", color: "#DC2626", bg: "#FEF2F2",
    img: "https://static.prod-images.emergentagent.com/jobs/807d24b9-8a0d-4ee2-b221-042517f188cc/images/dcf7426f583bb67af3f438e3f3c5c2a010b6740bd0a56d3fdd52ae5db48319f7.png" },
  { id: "kvm", name: "KVM & Enterprise Solutions", Icon: Server, desc: "ATEN KVM switches, thin clients & converters", count: "25+ Products", color: "#D97706", bg: "#FFFBEB",
    img: "https://static.prod-images.emergentagent.com/jobs/807d24b9-8a0d-4ee2-b221-042517f188cc/images/eee18479d37825bb04e0bc64d54ef743fb69fe14276a8dbdcdaf7bebd45a72d0.png" },
];

const WHY_US = [
  { Icon: Package, title: "Exhaustive Inventory", desc: "2000+ products across 5 verticals, always in stock." },
  { Icon: ShieldCheck, title: "Quality Assured", desc: "Stringent QC — zero out-of-box failure rate guarantee." },
  { Icon: TrendingDown, title: "Competitive Pricing", desc: "Aggressive scaled pricing for bulk & enterprise orders." },
  { Icon: Truck, title: "Agile Logistics", desc: "Fast dispatch with localized supply chain across Telangana." },
];

const PARTNERS = [
  {
    name: "ATEN", color: "#C41230", abbr: "AT",
    logos: ["https://upload.wikimedia.org/wikipedia/en/c/cf/ATEN_logo_blue.png"]
  },
  {
    name: "CommScope", color: "#005B9F", abbr: "CS",
    logos: ["https://upload.wikimedia.org/wikipedia/commons/4/48/Commscope-logo.png"]
  },
  {
    name: "Dell", color: "#007DB8", abbr: "DE",
    logos: ["https://upload.wikimedia.org/wikipedia/commons/4/48/Dell_Logo.svg"]
  },
  {
    name: "D-Link", color: "#003B7E", abbr: "DL",
    logos: ["https://static.prod-images.emergentagent.com/jobs/807d24b9-8a0d-4ee2-b221-042517f188cc/images/db30b21c9c46bad1cf2b7820c260b420257692e36d478cbe5a224535f8614203.png"]
  },
  {
    name: "Dyeton", color: "#E53935", abbr: "DY",
    logos: ["/assets/dyeton-logo.png"]
  },
  {
    name: "Frontech", color: "#D50000", abbr: "FT",
    logos: ["https://customer-assets.emergentagent.com/job_jain-infotech/artifacts/9oocer4p_frontech-logo-png_seeklogo-249419.png"]
  },
  {
    name: "Hikvision", color: "#CE1C22", abbr: "HK",
    logos: ["https://upload.wikimedia.org/wikipedia/commons/a/ad/Hikvision_logo.svg"]
  },
  {
    name: "Honeywell", color: "#ED1C24", abbr: "HW",
    logos: ["https://upload.wikimedia.org/wikipedia/commons/2/2a/Honeywell_logo.svg"]
  },
  {
    name: "HP", color: "#0096D6", abbr: "HP",
    logos: ["https://upload.wikimedia.org/wikipedia/commons/a/ad/HP_logo_2012.svg"]
  },
  {
    name: "Quantron", color: "#1B5FA8", abbr: "QT",
    logos: ["https://customer-assets.emergentagent.com/job_jain-infotech/artifacts/y9ap6xu6_Quantron%20Logo%20New.jpg.jpeg"]
  },
  {
    name: "Syrotech", color: "#FF6B00", abbr: "SY",
    logos: ["https://customer-assets.emergentagent.com/job_jain-infotech/artifacts/x2gwrikg_syrotech%20logo.avif"]
  },
  {
    name: "TP-Link", color: "#1AA537", abbr: "TP",
    logos: ["https://customer-assets.emergentagent.com/job_jain-infotech/artifacts/onuj77vd_Tp%20link%20logo.png"]
  },
];

function PartnerLogo({ name, logos, color, abbr }) {
  const [idx, setIdx] = useState(0);
  const showBadge = idx >= logos.length;

  return (
    <div
      data-testid={`partner-${name.toLowerCase().replace(/\s+/g, "-")}`}
      className="flex flex-col items-center gap-2.5"
    >
      {/* White card */}
      <div className="w-full h-[88px] bg-white flex items-center justify-center px-4 shadow-sm hover:shadow-lg transition-all duration-200 rounded-sm">
        {showBadge ? (
          <div
            className="w-14 h-10 flex items-center justify-center text-white font-black text-sm tracking-widest rounded"
            style={{ backgroundColor: color }}
          >
            {abbr}
          </div>
        ) : (
          <img
            src={logos[idx]}
            alt={name}
            onError={() => setIdx(i => i + 1)}
            className="h-12 max-w-[120px] object-contain transition-all duration-200"
          />
        )}
      </div>
      {/* Brand name below card */}
      <span className="text-[10px] font-semibold text-gray-400 text-center leading-tight tracking-widest uppercase">
        {name}
      </span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Tech-themed animated motion graphic used behind the hero section  */
/* ------------------------------------------------------------------ */
function HeroTechMotion() {
  // Fixed set of nodes so lines look intentional, not random
  const nodes = [
    { cx: 60,  cy: 80  }, { cx: 200, cy: 40  }, { cx: 340, cy: 120 },
    { cx: 480, cy: 60  }, { cx: 620, cy: 140 }, { cx: 760, cy: 90  },
    { cx: 120, cy: 220 }, { cx: 280, cy: 260 }, { cx: 440, cy: 220 },
    { cx: 600, cy: 280 }, { cx: 740, cy: 240 }, { cx: 80,  cy: 360 },
    { cx: 240, cy: 400 }, { cx: 400, cy: 360 }, { cx: 560, cy: 420 },
    { cx: 720, cy: 380 },
  ];
  const lines = [
    [0,1],[1,2],[2,3],[3,4],[4,5],
    [0,6],[1,7],[2,8],[3,8],[4,9],[5,10],
    [6,7],[7,8],[8,9],[9,10],
    [6,11],[7,12],[8,13],[9,14],[10,15],
    [11,12],[12,13],[13,14],[14,15],
  ];

  return (
    <div
      className="pointer-events-none absolute inset-0 overflow-hidden"
      aria-hidden="true"
      data-testid="hero-tech-motion"
    >
      {/* Grid backdrop */}
      <svg className="absolute inset-0 w-full h-full opacity-[0.35]" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern id="grid-pattern" width="48" height="48" patternUnits="userSpaceOnUse">
            <path d="M 48 0 L 0 0 0 48" fill="none" stroke="#0F62FE" strokeWidth="0.5" />
          </pattern>
          <radialGradient id="grid-fade" cx="50%" cy="50%" r="60%">
            <stop offset="0%" stopColor="white" stopOpacity="1" />
            <stop offset="100%" stopColor="white" stopOpacity="0" />
          </radialGradient>
          <mask id="grid-mask">
            <rect width="100%" height="100%" fill="url(#grid-fade)" />
          </mask>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid-pattern)" mask="url(#grid-mask)" />
      </svg>

      {/* Network mesh */}
      <svg
        viewBox="0 0 800 460"
        preserveAspectRatio="xMidYMid slice"
        className="absolute inset-0 w-full h-full opacity-40 md:opacity-100"
      >
        {/* connecting lines */}
        {lines.map(([a, b], i) => {
          const n1 = nodes[a], n2 = nodes[b];
          return (
            <motion.line
              key={`l-${i}`}
              x1={n1.cx} y1={n1.cy} x2={n2.cx} y2={n2.cy}
              stroke="#0F62FE"
              strokeOpacity="0.25"
              strokeWidth="1"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ pathLength: 1, opacity: 0.35 }}
              transition={{ duration: 1.6, delay: 0.05 * i, ease: "easeOut" }}
            />
          );
        })}

        {/* pulsing nodes */}
        {nodes.map((n, i) => (
          <g key={`n-${i}`}>
            <motion.circle
              cx={n.cx} cy={n.cy} r="8"
              fill="#0F62FE" fillOpacity="0.15"
              animate={{ r: [8, 16, 8], opacity: [0.35, 0, 0.35] }}
              transition={{ duration: 3, repeat: Infinity, delay: (i % 6) * 0.4, ease: "easeInOut" }}
            />
            <motion.circle
              cx={n.cx} cy={n.cy} r="3"
              fill="#0F62FE"
              animate={{ opacity: [0.6, 1, 0.6] }}
              transition={{ duration: 2.4, repeat: Infinity, delay: (i % 6) * 0.3 }}
            />
          </g>
        ))}

        {/* data packets travelling along a few lines */}
        {[
          { from: 0, to: 4, dur: 4, delay: 0 },
          { from: 6, to: 10, dur: 5, delay: 1.2 },
          { from: 11, to: 15, dur: 4.5, delay: 0.6 },
          { from: 2, to: 13, dur: 5.5, delay: 2 },
        ].map((p, i) => {
          const a = nodes[p.from], b = nodes[p.to];
          return (
            <motion.circle
              key={`packet-${i}`}
              r="3"
              fill="#00BCD4"
              initial={{ cx: a.cx, cy: a.cy, opacity: 0 }}
              animate={{ cx: [a.cx, b.cx], cy: [a.cy, b.cy], opacity: [0, 1, 1, 0] }}
              transition={{ duration: p.dur, repeat: Infinity, delay: p.delay, ease: "linear" }}
            />
          );
        })}
      </svg>

      {/* Floating binary/hex chips */}
      <motion.div
        className="hidden md:block absolute top-8 left-6 text-[10px] font-mono text-[#0F62FE]/40 tracking-widest select-none"
        animate={{ y: [0, -8, 0], opacity: [0.35, 0.6, 0.35] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      >
        01001010 01001001 01010100
      </motion.div>
      <motion.div
        className="hidden md:block absolute bottom-10 left-1/3 text-[10px] font-mono text-[#00BCD4]/40 tracking-widest select-none"
        animate={{ y: [0, 8, 0], opacity: [0.3, 0.55, 0.3] }}
        transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 1 }}
      >
        0xAF3C · TCP/IP · CAT6 · 10G
      </motion.div>
      <motion.div
        className="hidden md:block absolute top-1/2 right-4 text-[10px] font-mono text-[#0F62FE]/40 tracking-widest select-none"
        animate={{ y: [0, -6, 0], opacity: [0.35, 0.6, 0.35] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 2 }}
      >
        &lt;/&gt; HDMI 2.1 · 8K · 48Gbps
      </motion.div>
    </div>
  );
}

export default function Home() {
  return (
    <div>
      {/* Hero */}
      <section className="relative bg-white overflow-hidden" data-testid="hero-section">
        <HeroTechMotion />
        <div className="relative max-w-7xl mx-auto px-6 py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
              <span className="inline-block text-xs font-bold uppercase tracking-widest text-[#0F62FE] bg-blue-50 border border-blue-200 px-3 py-1.5 mb-5">
                Trusted IT Distributor Since 2017
              </span>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black text-[#111827] leading-[1.1] mb-5">
                Your Trusted <br />
                <span className="text-[#0F62FE]">IT Infrastructure</span><br />
                Partner
              </h1>
              <p className="text-base text-[#4B5563] mb-8 leading-relaxed max-w-xl">
                Premier supplier of cables, networking gear, CCTV solutions, and enterprise hardware for corporate clients, IT dealers, and system integrators across Telangana.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/products"
                  data-testid="hero-view-products-btn"
                  className="inline-flex items-center gap-2 bg-[#0F62FE] hover:bg-[#0052CC] text-white font-semibold px-6 py-3.5 transition-all duration-200 hover:shadow-lg"
                >
                  Explore Products <ArrowRight size={16} />
                </Link>
                <a
                  href="/assets/jain-infotech-brochure.pdf"
                  download
                  data-testid="hero-download-brochure-btn"
                  className="inline-flex items-center gap-2 border border-[#2D3748] text-[#2D3748] hover:bg-gray-50 font-semibold px-6 py-3.5 transition-all duration-200"
                >
                  <Download size={16} /> Download Brochure
                </a>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="relative">
              <div className="relative overflow-hidden shadow-2xl">
                <img
                  src="https://images.pexels.com/photos/3182812/pexels-photo-3182812.jpeg?auto=compress&cs=tinysrgb&w=1200"
                  alt="IT Professionals collaborating in a bright office"
                  className="w-full h-[400px] lg:h-[460px] object-cover"
                />
                {/* subtle brand-blue tint for cohesion — kept light so the image stays bright */}
                <div className="absolute inset-0 bg-gradient-to-tr from-[#0F62FE]/25 via-transparent to-[#00BCD4]/10" />
                {/* Animated scanline / glow accents overlay */}
                <motion.div
                  className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-transparent via-[#00BCD4] to-transparent"
                  animate={{ y: [0, 460, 0] }}
                  transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                />
                <div className="absolute bottom-4 left-4 flex items-center gap-2 bg-white/85 backdrop-blur-sm px-3 py-1.5 border border-white shadow">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#22C55E] animate-pulse" />
                  <span className="text-[10px] font-mono uppercase tracking-widest text-[#111827]">Enterprise-grade</span>
                </div>
              </div>
              <div className="absolute -bottom-4 -left-4 bg-[#0F62FE] text-white px-5 py-3 shadow-xl">
                <p className="text-2xl font-black font-heading">07+</p>
                <p className="text-xs font-medium text-blue-200">Years of Trust</p>
              </div>
              <div className="absolute -top-4 -right-4 bg-[#00BCD4] text-white px-5 py-3 shadow-xl">
                <p className="text-2xl font-black font-heading">2000+</p>
                <p className="text-xs font-medium text-cyan-100">Products</p>
              </div>
              <HeroSceneCarousel />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-[#2D3748] py-10" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {STATS.map((s, i) => (
              <motion.div key={s.label} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.1 }} className="text-center">
                <p className="text-3xl lg:text-4xl font-black text-[#0F62FE] font-heading" data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g,"-")}`}>{s.value}</p>
                <p className="text-gray-300 text-sm mt-1">{s.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Categories */}
      <section className="py-16 bg-[#F3F4F6]" data-testid="product-categories-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="font-heading text-3xl lg:text-4xl font-black text-[#111827] mb-3">Our Product Portfolio</h2>
            <p className="text-[#4B5563] text-base max-w-lg mx-auto">Five comprehensive product verticals covering all your IT infrastructure needs</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            {CATEGORIES.slice(0, 3).map(({ id, name, Icon, desc, count, color, bg, img }, i) => (
              <motion.div key={id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.1 }}>
                <Link to={`/products?cat=${id}`} data-testid={`cat-card-${id}`} className="flex flex-col bg-white border border-gray-200 hover:border-[#0F62FE] hover:shadow-md transition-all duration-200 group h-full overflow-hidden">
                  {/* Category image */}
                  <div className="relative h-32 overflow-hidden">
                    <img src={img} alt={name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    <div className="absolute inset-0 bg-gradient-to-t from-white/80 to-transparent" />
                    <div className="absolute bottom-2 left-3 flex items-center gap-1.5">
                      <div className="p-1.5" style={{ backgroundColor: bg }}>
                        <Icon size={14} style={{ color }} />
                      </div>
                      <span className="text-[10px] font-bold text-gray-600 bg-white/90 px-1.5 py-0.5">{count}</span>
                    </div>
                  </div>
                  <div className="p-4 flex flex-col flex-1">
                    <h3 className="font-heading font-bold text-[#111827] text-sm group-hover:text-[#0F62FE] transition-colors mb-1">{name}</h3>
                    <p className="text-xs text-[#4B5563] flex-1">{desc}</p>
                    <div className="mt-3 flex items-center gap-1 text-xs font-semibold text-[#0F62FE]">
                      View Products <ArrowRight size={12} />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {CATEGORIES.slice(3).map(({ id, name, Icon, desc, count, color, bg, img }, i) => (
              <motion.div key={id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.1 }}>
                <Link to={`/products?cat=${id}`} data-testid={`cat-card-${id}`} className="flex bg-white border border-gray-200 hover:border-[#0F62FE] hover:shadow-md transition-all duration-200 group overflow-hidden">
                  <div className="relative w-28 shrink-0">
                    <img src={img} alt={name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent to-white/60" />
                  </div>
                  <div className="p-4 flex flex-col justify-center">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="p-1.5" style={{ backgroundColor: bg }}>
                        <Icon size={14} style={{ color }} />
                      </div>
                      <span className="text-[10px] font-bold text-gray-400">{count}</span>
                    </div>
                    <h3 className="font-heading font-bold text-[#111827] text-sm group-hover:text-[#0F62FE] transition-colors">{name}</h3>
                    <p className="text-xs text-[#4B5563] mt-1">{desc}</p>
                    <div className="mt-2 flex items-center gap-1 text-xs font-semibold text-[#0F62FE]">View Products <ArrowRight size={12} /></div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/products" data-testid="view-all-products-btn" className="inline-flex items-center gap-2 border border-[#0F62FE] text-[#0F62FE] hover:bg-[#0F62FE] hover:text-white font-semibold px-6 py-3 transition-all duration-200">
              View Complete Catalogue <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-white" data-testid="why-choose-preview-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block text-xs font-bold uppercase tracking-widest text-[#0F62FE] bg-blue-50 border border-blue-200 px-3 py-1.5 mb-4">Why Partner With Us</span>
              <h2 className="font-heading text-3xl lg:text-4xl font-black text-[#111827] mb-4 leading-tight">Your Competitive Edge in IT Procurement</h2>
              <p className="text-[#4B5563] text-base mb-8 leading-relaxed">
                7+ years of operational excellence, stringent quality control, and an exhaustive inventory make Jain Infotech the preferred distributor for enterprises and system integrators.
              </p>
              <Link to="/why-choose-us" data-testid="why-choose-us-cta-btn" className="inline-flex items-center gap-2 bg-[#0F62FE] hover:bg-[#0052CC] text-white font-semibold px-5 py-3 transition-all duration-200 hover:shadow-lg">
                Learn More <ArrowRight size={14} />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {WHY_US.map(({ Icon, title, desc }, i) => (
                <motion.div key={title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.1 }}
                  className="p-5 bg-[#F3F4F6] border border-gray-200 hover:border-[#0F62FE] transition-all duration-200">
                  <Icon size={24} className="text-[#0F62FE] mb-3" />
                  <h4 className="font-heading font-bold text-[#111827] text-sm mb-1.5">{title}</h4>
                  <p className="text-xs text-[#4B5563] leading-relaxed">{desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Industries bar */}
      <section className="py-10 bg-[#2D3748]" data-testid="industries-bar-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="font-heading font-bold text-white text-lg mb-1">Industries We Serve</h3>
              <p className="text-gray-400 text-sm">Tailored solutions for every enterprise sector</p>
            </div>
            <div className="flex flex-wrap gap-3">
              {["Corporate Offices","IT Dealers","System Integrators","Academia","Government"].map((ind) => (
                <span key={ind} className="px-4 py-2 bg-white/10 text-gray-200 text-xs font-medium border border-white/20 hover:bg-[#0F62FE] hover:border-[#0F62FE] transition-all cursor-default">
                  {ind}
                </span>
              ))}
            </div>
            <Link to="/industries" data-testid="industries-explore-btn" className="inline-flex items-center gap-2 border border-white/30 text-white hover:bg-white hover:text-[#2D3748] text-sm font-semibold px-5 py-2.5 transition-all duration-200 whitespace-nowrap">
              Explore <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* Partners */}
      <section className="py-16 bg-[#1E2A3A]" data-testid="partners-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl lg:text-3xl font-bold text-white mb-2">A portfolio backed by industry-leading manufacturers.</h2>
            <p className="text-gray-400 text-sm">Authorized distributor for leading global and domestic IT brands</p>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-4">
            {PARTNERS.map((p) => <PartnerLogo key={p.name} {...p} />)}
          </div>
          <p className="text-center text-gray-600 text-[10px] mt-8 uppercase tracking-widest">
            All trademarks are property of their respective owners
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#0F62FE]" data-testid="cta-banner-section">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
            <h2 className="font-heading text-3xl lg:text-4xl font-black text-white mb-4">Ready to Power Your IT Infrastructure?</h2>
            <p className="text-blue-100 text-base mb-8 max-w-lg mx-auto">
              Get competitive quotes for bulk IT hardware orders. Serving enterprises, resellers, and government institutions.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/contact" data-testid="cta-get-quote-btn" className="inline-flex items-center gap-2 bg-white text-[#0F62FE] hover:bg-blue-50 font-semibold px-7 py-3.5 transition-all duration-200 hover:shadow-lg">
                Get a Quote <ArrowRight size={16} />
              </Link>
              <a
                href="https://wa.me/918500914557?text=Hi%20Jain%20Infotech%2C%20I%20would%20like%20to%20get%20a%20quote."
                target="_blank" rel="noreferrer"
                data-testid="cta-whatsapp-btn"
                className="inline-flex items-center gap-2 border-2 border-white text-white hover:bg-white hover:text-[#0F62FE] font-semibold px-7 py-3.5 transition-all duration-200"
              >
                <FaWhatsapp size={18} /> WhatsApp Us
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
